import { useState, useMemo } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import {
  useAdminAuditLogs, useAdminAuditLogMeta, useAdminAuditLogStats,
} from '@/hooks/use-admin';
import type { AuditLogRow } from '@shared/types/api';
import { toast } from 'sonner';
import {
  getActionBadgeTone, formatFullDateTime, formatRelativeTime,
  formatUserAgent,
} from './audit-logs/audit-log-utils';
import { resolveActionTitle } from './audit-logs/audit-log-semantic-diff';
import { AuditLogDetailDialog } from './audit-logs/audit-log-detail-modal';

export function AdminAuditLogsPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAction, setSelectedAction] = useState<string>('ALL');
  const [selectedEntityType, setSelectedEntityType] = useState<string>('ALL');
  const [dateRangeFilter, setDateRangeFilter] = useState<'ALL' | 'TODAY' | 'WEEK' | 'MONTH'>('ALL');
  const [inspectLog, setInspectLog] = useState<AuditLogRow | null>(null);

  // Compute fromDate based on range filter
  const { fromDate, toDate } = useMemo(() => {
    if (dateRangeFilter === 'ALL') return { fromDate: undefined, toDate: undefined };
    const now = new Date();
    if (dateRangeFilter === 'TODAY') {
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return { fromDate: today.toISOString(), toDate: undefined };
    }
    if (dateRangeFilter === 'WEEK') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return { fromDate: weekAgo.toISOString(), toDate: undefined };
    }
    if (dateRangeFilter === 'MONTH') {
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);
      return { fromDate: monthAgo.toISOString(), toDate: undefined };
    }
    return { fromDate: undefined, toDate: undefined };
  }, [dateRangeFilter]);

  const queryParams = useMemo(() => ({
    page,
    pageSize,
    q: searchQuery.trim() || undefined,
    action: selectedAction !== 'ALL' ? selectedAction : undefined,
    entityType: selectedEntityType !== 'ALL' ? selectedEntityType : undefined,
    fromDate,
    toDate,
  }), [page, pageSize, searchQuery, selectedAction, selectedEntityType, fromDate, toDate]);

  const { data, isLoading, isError, refetch } = useAdminAuditLogs(queryParams);
  const { data: meta } = useAdminAuditLogMeta();
  const { data: stats } = useAdminAuditLogStats();

  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const isFiltered =
    Boolean(searchQuery.trim()) ||
    selectedAction !== 'ALL' ||
    selectedEntityType !== 'ALL' ||
    dateRangeFilter !== 'ALL';

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedAction('ALL');
    setSelectedEntityType('ALL');
    setDateRangeFilter('ALL');
    setPage(1);
  };

  const handleExportCsv = () => {
    if (!data?.items || data.items.length === 0) {
      toast.error('No audit logs to export');
      return;
    }

    const headers = [
      'Log ID',
      'Timestamp',
      'Action Title',
      'Action Code',
      'Actor Name',
      'Actor Email',
      'Actor Role',
      'Target Entity',
      'Entity Type',
      'IP Address',
      'Browser / Device',
    ];

    const rows = data.items.map((log) => {
      const actorName = log.actor
        ? [log.actor.firstName, log.actor.lastName].filter(Boolean).join(' ') || log.actor.email
        : 'System';
      const actorRole = log.actor?.adminRole || log.actor?.role || 'System';
      const targetLabel = log.entityLabel || (log.entityId ? `#${log.entityId.slice(0, 8)}...` : '');
      const device = formatUserAgent(log.userAgent) || (log.userAgent || '');

      return [
        log.id,
        log.createdAt,
        resolveActionTitle(log.action),
        log.action,
        actorName,
        log.actor?.email || 'System',
        actorRole,
        targetLabel,
        log.entityType || '',
        log.ipAddress || '',
        device,
      ];
    });

    const escapeCell = (val: unknown) => `"${String(val ?? '').replace(/"/g, '""')}"`;

    const csvBody = [
      headers.map(escapeCell).join(','),
      ...rows.map((row) => row.map(escapeCell).join(',')),
    ].join('\r\n');

    // Prepend UTF-8 BOM (\uFEFF) so Excel natively recognizes UTF-8 without SYLK/corruption warnings
    const blob = new Blob(['\uFEFF' + csvBody], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `ecregi-audit-logs-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Audit log export downloaded as CSV');
  };

  return (
    <div className="w-full space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">Audit Logs</h1>
            <span className="rounded-full bg-brand-100 text-brand-900 px-2.5 py-0.5 text-xs font-bold border border-brand-200">
              {stats?.totalLogs ?? data?.total ?? 0} total
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Real-time forensic activity log, state changes, and administrative actions across EcRegi.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => refetch()}
            className="gap-1.5 px-3 py-1.5 text-xs"
            disabled={isLoading}
          >
            <svg className={cn('w-4 h-4', isLoading && 'animate-spin')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </Button>

          <Button
            variant="outline"
            onClick={handleExportCsv}
            className="gap-1.5 px-3 py-1.5 text-xs text-brand-900 hover:bg-brand-50"
            disabled={!data?.items?.length}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </Button>
        </div>
      </div>

      {/* Metrics Banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs">
          <span className="text-xs font-semibold uppercase text-gray-500 block mb-1">Total Logs</span>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-gray-900">{stats?.totalLogs ?? '—'}</span>
            <span className="text-[11px] text-gray-400">records</span>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs">
          <span className="text-xs font-semibold uppercase text-gray-500 block mb-1">Today's Activity</span>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-brand-900">{stats?.todayLogs ?? '—'}</span>
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live
            </span>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs">
          <span className="text-xs font-semibold uppercase text-gray-500 block mb-1">Active Admins</span>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-gray-900">{stats?.uniqueActors ?? '—'}</span>
            <span className="text-[11px] text-gray-400">actors</span>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs">
          <span className="text-xs font-semibold uppercase text-gray-500 block mb-1">Top Event Type</span>
          <div className="truncate">
            <span className="text-base font-bold text-gray-900 block truncate" title={stats?.topAction || ''}>
              {stats?.topAction || '—'}
            </span>
            <span className="text-[11px] text-gray-400">most frequent</span>
          </div>
        </div>
      </div>

      {/* Interactive Filter Toolbar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Search Box */}
          <div className="relative">
            <Input
              type="text"
              placeholder="Search action, actor, entity ID, or IP..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="text-xs pl-8"
            />
            <svg
              className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Action Filter Dropdown */}
          <select
            value={selectedAction}
            onChange={(e) => {
              setSelectedAction(e.target.value);
              setPage(1);
            }}
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-xs focus:border-brand-500 focus:outline-hidden focus:ring-1 focus:ring-brand-500"
          >
            <option value="ALL">All Actions (Any)</option>
            {meta?.actions?.map((act) => (
              <option key={act} value={act}>
                {act}
              </option>
            ))}
          </select>

          {/* Entity Type Dropdown */}
          <select
            value={selectedEntityType}
            onChange={(e) => {
              setSelectedEntityType(e.target.value);
              setPage(1);
            }}
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-xs focus:border-brand-500 focus:outline-hidden focus:ring-1 focus:ring-brand-500"
          >
            <option value="ALL">All Entity Types</option>
            {meta?.entityTypes?.map((ent) => (
              <option key={ent} value={ent}>
                {ent}
              </option>
            ))}
          </select>

          {/* Date Range Selector */}
          <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50/50 p-1">
            {(['ALL', 'TODAY', 'WEEK', 'MONTH'] as const).map((range) => (
              <button
                key={range}
                type="button"
                onClick={() => {
                  setDateRangeFilter(range);
                  setPage(1);
                }}
                className={cn(
                  'flex-1 text-[11px] font-semibold py-1 rounded-md transition-colors text-center',
                  dateRangeFilter === range
                    ? 'bg-white text-brand-900 shadow-xs border border-gray-200'
                    : 'text-gray-600 hover:text-gray-900',
                )}
              >
                {range === 'ALL'
                  ? 'All'
                  : range === 'TODAY'
                    ? 'Today'
                    : range === 'WEEK'
                      ? '7d'
                      : '30d'}
              </button>
            ))}
          </div>
        </div>

        {isFiltered && (
          <div className="flex items-center justify-between border-t border-gray-100 pt-2 text-xs">
            <span className="text-gray-500">
              Showing filtered results ({data?.total ?? 0} matches)
            </span>
            <button
              type="button"
              onClick={resetFilters}
              className="text-brand-800 hover:text-brand-950 font-semibold"
            >
              Reset all filters
            </button>
          </div>
        )}
      </div>

      {/* Top Pagination */}
      <div className="flex items-center justify-end">
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
          isLoading={isLoading}
          showBorder={false}
          showRange={false}
          showPageSize={false}
          compact
        />
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-gray-500 space-y-3">
            <div className="inline-block w-8 h-8 border-3 border-brand-900 border-t-transparent rounded-full animate-spin" />
            <p className="text-xs font-medium">Loading audit trail entries...</p>
          </div>
        ) : isError ? (
          <div className="p-12 text-center text-gray-600 space-y-3">
            <p className="text-sm font-semibold text-rose-600">Failed to load audit logs</p>
            <Button variant="outline" className="px-3 py-1.5 text-xs" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : !data?.items?.length ? (
          <div className="p-12 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-gray-100 mx-auto flex items-center justify-center text-gray-400">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-sm font-bold text-gray-900">No audit logs found</h3>
            <p className="text-xs text-gray-500 max-w-sm mx-auto">
              {isFiltered
                ? 'No activity matches your active search and filter criteria. Try clearing filters.'
                : 'No mutation events have been recorded in the audit trail yet.'}
            </p>
            {isFiltered && (
              <Button variant="outline" className="px-3 py-1.5 text-xs" onClick={resetFilters}>
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/75 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 whitespace-nowrap">Timestamp</th>
                  <th className="px-4 py-3 whitespace-nowrap">Actor</th>
                  <th className="px-4 py-3 whitespace-nowrap">Action</th>
                  <th className="px-4 py-3 whitespace-nowrap">Target Entity</th>
                  <th className="px-4 py-3 whitespace-nowrap">Network Info</th>
                  <th className="px-4 py-3 text-right whitespace-nowrap">Inspect</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.items.map((log) => {
                  const badge = getActionBadgeTone(log.action);
                  const actorEmail = log.actor?.email || 'System';
                  const actorName = log.actor
                    ? [log.actor.firstName, log.actor.lastName].filter(Boolean).join(' ') || actorEmail
                    : 'System';

                  return (
                    <tr
                      key={log.id}
                      className="hover:bg-gray-50/60 transition-colors cursor-pointer"
                      onClick={() => setInspectLog(log)}
                    >
                      {/* Timestamp */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-semibold text-gray-900 block">
                          {formatRelativeTime(log.createdAt)}
                        </span>
                        <span className="text-[10px] text-gray-400 block" title={log.createdAt}>
                          {formatFullDateTime(log.createdAt)}
                        </span>
                      </td>

                      {/* Actor */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-brand-800 text-white flex items-center justify-center font-bold text-[10px] shrink-0">
                            {actorEmail[0].toUpperCase()}
                          </div>
                          <div className="truncate max-w-[160px]">
                            <span className="font-semibold text-gray-900 block truncate" title={actorName}>
                              {actorName}
                            </span>
                            <span className="text-[10px] text-gray-400 block truncate" title={actorEmail}>
                              {actorEmail}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Action Badge */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border shadow-2xs',
                            badge.bg,
                            badge.text,
                          )}
                          title={`Technical Event: ${log.action}`}
                        >
                          <span className={cn('w-1.5 h-1.5 rounded-full', badge.dot)} />
                          {resolveActionTitle(log.action)}
                        </span>
                      </td>

                      {/* Target Entity */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        {log.entityLabel ? (
                          <div>
                            <span className="font-semibold text-gray-900 block truncate max-w-[200px]" title={log.entityLabel}>
                              {log.entityLabel}
                            </span>
                            <div className="flex items-center gap-1 text-[10px] text-gray-400">
                              <span>{log.entityType}</span>
                              {log.entityId && <span>• #{log.entityId.slice(0, 8)}...</span>}
                            </div>
                          </div>
                        ) : log.entityType ? (
                          <div>
                            <span className="font-semibold text-gray-800 block">{log.entityType}</span>
                            {log.entityId && (
                              <span className="font-mono text-[10px] text-gray-400 block truncate max-w-[130px]" title={log.entityId}>
                                #{log.entityId.slice(0, 8)}...
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>

                      {/* Network Info */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-mono text-gray-700 block text-[11px]">
                          {log.ipAddress || '—'}
                        </span>
                        {log.userAgent && (
                          <span
                            className="text-[10px] text-gray-400 block truncate max-w-[140px]"
                            title={log.userAgent}
                          >
                            {formatUserAgent(log.userAgent)}
                          </span>
                        )}
                      </td>

                      {/* Inspect Button */}
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <Button
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            setInspectLog(log);
                          }}
                          className="h-7 px-2 text-xs text-brand-800 hover:text-brand-950 hover:bg-brand-50"
                        >
                          View Diff
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {total > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            isLoading={isLoading}
          />
        )}
      </div>

      {/* Inspect Detail Modal */}
      {inspectLog && (
        <AuditLogDetailDialog log={inspectLog} onClose={() => setInspectLog(null)} />
      )}
    </div>
  );
}
